import { Badge, Button, Tooltip } from "@mantine/core";
import * as React from "react";
import { Role } from "../../../../types/persons/Role";
import { IPerson } from "../../../../types/persons/person";
import { Privilege } from "../../../../types/persons/privilege";
import { IShiftEnrolment } from "../../../../types/shifts/ShiftEnrolment";
import { ShiftEnrolmentType } from "../../../../types/shifts/enums/ShiftEnrolmentType";
import { CenteredLoader } from "../../../common/loader";
import { RoleIconDisplay, RoleIconSelector } from "./roleIcons";

export interface PersonRoleCellProps {
  person: IPerson;
  referee: IShiftEnrolment;
  pid: string;
  onDelete: Function;
  onChange: Function;
  toggleEdit: (referee?: IShiftEnrolment) => void;
}

const PersonRoleCell: React.FunctionComponent<PersonRoleCellProps> = ({
  person,
  pid,
  referee,
  toggleEdit,
}) => {
  const hasShiftSupervisorPrivilege = Privilege.hasPrivilege(
    referee.privilege ?? Privilege.none,
    Privilege.shiftSupervisor,
  )
  const supervisorBadge = hasShiftSupervisorPrivilege ? (
    <Tooltip withArrow label="Shift supervisor privilege" position="top" withinPortal>
      <Badge size="sm" color="indigo" variant="light">
        EDIT
      </Badge>
    </Tooltip>
  ) : null

  return (
    <tr>
      {person ? (
        <td key={`cell-${pid}`}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span>{person.fullName}</span>
            {supervisorBadge}
          </div>
        </td>
      ) : (
        <div style={{ paddingLeft: "40%", paddingTop: "5px" }}>
          <CenteredLoader size={20} />
        </div>
      )}
      <td>{ShiftEnrolmentType.toString(referee.type)}</td>
      <td>
        <RoleIconDisplay value={referee.role} />
      </td>
      <td>
        <Button
          size="sm"
          color="link"
          id={`PersonRoleButton-${pid}`}
          style={{ color: "inherit" }}
          onClick={() => toggleEdit(referee)}
        >
          Edit
        </Button>
      </td>
    </tr>
  )
}

const PersonRoleEditCell: React.FunctionComponent<PersonRoleCellProps> = ({
  person,
  pid,
  referee,
  toggleEdit,
  onChange,
  onDelete,
}) => {
  const [changes, setInternalChanges] = React.useState<object>({});
  const setChanges = (moreChanges) =>
    setInternalChanges({ ...changes, ...moreChanges });
  const refs = { ...referee, ...changes };
  const privilegeValue = refs.privilege ?? Privilege.none;

  const addOrRemoveRole = (roleValue: Role, checked: boolean) => {
    const newRole = checked
      ? Role.addRole(refs.role, roleValue)
      : Role.removeRole(refs.role, roleValue);
    setChanges({ role: newRole });
  };

  const toggleShiftSupervisorPrivilege = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = event.currentTarget.checked;
    setChanges({
      privilege: checked ? Privilege.shiftSupervisor : Privilege.none,
    });
  };

  const hasShiftSupervisorPrivilege = Privilege.hasPrivilege(
    privilegeValue,
    Privilege.shiftSupervisor,
  )
  const supervisorBadge = hasShiftSupervisorPrivilege ? (
    <Tooltip withArrow label="Shift supervisor privilege" position="top" withinPortal>
      <Badge size="sm" color="indigo" variant="light">
        EDIT
      </Badge>
    </Tooltip>
  ) : null

  const personCell = person ? (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>{person.fullName}</span>
      {supervisorBadge}
    </div>
  ) : (
    <div style={{ paddingLeft: "40%", paddingTop: "5px" }}>
      <CenteredLoader size={20} />
    </div>
  );

  const editingRowStyle: React.CSSProperties = {
    backgroundColor: "var(--color-bg-disabled-card)",
    boxShadow: "inset 3px 0 0 var(--color-brand-medium)",
  };
  const editingCellStyle: React.CSSProperties = {
    padding: "12px 16px",
  };

  return (
    <tr style={editingRowStyle}>
      <td key={`cell-${pid}`} style={editingCellStyle}>
        {personCell}
      </td>
      <td style={editingCellStyle}>
        <select
          value={refs.type}
          onChange={(e) =>
            setChanges({ type: parseInt(e.currentTarget.value) })
          }
        >
          {ShiftEnrolmentType.all().map((s) => (
            <option key={s} value={s}>
              {ShiftEnrolmentType.toString(s)}
            </option>
          ))}
        </select>
      </td>
      <td style={editingCellStyle}>
        <RoleIconSelector value={refs.role} onToggle={addOrRemoveRole} />
        <div style={{ marginTop: "10px" }}>
          <Tooltip
            label="Controls whether this enrolment has shift supervisor privileges."
            withArrow
            position="top"
            withinPortal
          >
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                name="privilege"
                checked={Privilege.hasPrivilege(
                  privilegeValue,
                  Privilege.shiftSupervisor,
                )}
                onChange={toggleShiftSupervisorPrivilege}
              />
              <span>Shift supervisor privilege</span>
            </label>
          </Tooltip>
        </div>
      </td>
      <td style={{ ...editingCellStyle, verticalAlign: "middle" }}>
        <Button
          color="link"
          size="sm"
          onClick={() =>
            Object.keys(changes).length > 0 ? onChange(refs) : toggleEdit()
          }
          style={{ color: "inherit" }}
        >
          Done
        </Button>
        <br />
        <br />
        <Button
          color="link"
          size="sm"
          onClick={() => onDelete(pid)}
          style={{ color: "#dc3545" }}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
};

const cells = {
  PersonRoleCell,
  PersonRoleEditCell,
};

export default cells;
